// Hook para gestionar datos del calendario desde el panel administrativo
import { useQuery } from '@tanstack/react-query'
import { ApiClient } from '../services/api'
import { parseLocalDateString } from '../utils/dateHelpers'

// =============================================================================
// INTERFACES
// =============================================================================

export interface EspacioCalendario {
  id: string
  codigo: string
  nombre: string
  tipo: string
  tipo_espacio_id?: string  // ID del tipo de espacio
  capacidad: number
  tarifa_base_tipo: number
  ubicacion_x?: number
  ubicacion_y?: number
}

export interface ReservaCalendario {
  id: string
  codigo?: string
  espacio_id: string
  espacio_nombre: string
  cliente_nombre?: string
  fecha_entrada: string  // YYYY-MM-DD
  fecha_salida: string   // YYYY-MM-DD
  estado: string
  estado_pago: string
  cantidad_huespedes: number
  monto_total: number
  unidad_reserva?: string  // 'dia' o 'noche'
}

export interface DisponibilidadEspacio {
  espacio_id: string
  espacio_nombre: string
  disponible: boolean
  fecha_inicio: string  // YYYY-MM-DD
  fecha_fin: string     // YYYY-MM-DD
  reservas: ReservaCalendario[]
  bloques_ocupados: Array<{ fecha_inicio: string; fecha_fin: string }>
}

export interface CalendarioDisponibilidad {
  fecha_desde: string  // YYYY-MM-DD
  fecha_hasta: string  // YYYY-MM-DD
  espacios: DisponibilidadEspacio[]
}

export interface ReservaDetalleDia {
  id: string
  espacio_nombre: string
  cliente_nombre: string
  estado: string
  estado_pago: string
  cantidad_huespedes: number
  monto_total: number
}

export interface DiaOcupacion {
  fecha: string  // YYYY-MM-DD
  num_reservas: number
  ocupacion_porcentaje: number
  reservas: ReservaDetalleDia[]
}

export interface CalendarioResumen {
  ocupacion_promedio: number
  espacios_ocupados: number
  espacios_disponibles: number
  reservas_este_mes: number
}

export interface CalendarioDiasOcupacion {
  mes: string  // YYYY-MM
  dias: DiaOcupacion[]
}

// =============================================================================
// QUERY KEYS
// =============================================================================

export const CALENDARIO_QUERY_KEYS = {
  ESPACIOS: 'calendario-espacios',
  DISPONIBILIDAD: 'calendario-disponibilidad',
  RESERVAS: 'calendario-reservas',
  RESUMEN: 'calendario-resumen',
  DIAS_OCUPACION: 'calendario-dias-ocupacion',
} as const

// =============================================================================
// HOOKS
// =============================================================================

/**
 * Hook para obtener lista de espacios del calendario
 * Útil para filtros y dropdowns
 */
export const useCalendarioEspacios = () => {
  return useQuery({
    queryKey: [CALENDARIO_QUERY_KEYS.ESPACIOS],
    queryFn: async () => {
      const response = await ApiClient.get<EspacioCalendario[]>(
        '/api/admin-protected/calendario/espacios'
      )
      return response
    },
    staleTime: 10 * 60 * 1000, // 10 minutos
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  })
}

/**
 * Hook para obtener disponibilidad de espacios para un rango de fechas
 * Retorna lista de espacios con sus reservas en el rango especificado
 *
 * @param fechaDesde - Fecha inicial (YYYY-MM-DD)
 * @param fechaHasta - Fecha final (YYYY-MM-DD)
 * @param espacioId - (Opcional) Filtrar por espacio específico
 * @param tipoEspacioId - (Opcional) Filtrar por tipo de espacio
 */
export const useCalendarioDisponibilidad = (
  fechaDesde: string,
  fechaHasta: string,
  espacioId?: string,
  tipoEspacioId?: string
) => {
  return useQuery({
    queryKey: [
      CALENDARIO_QUERY_KEYS.DISPONIBILIDAD,
      { fechaDesde, fechaHasta, espacioId, tipoEspacioId },
    ],
    queryFn: async () => {
      let url = `/api/admin-protected/calendario/disponibilidad?fecha_desde=${fechaDesde}&fecha_hasta=${fechaHasta}`

      if (espacioId) {
        url += `&espacio_id=${espacioId}`
      }

      if (tipoEspacioId) {
        url += `&tipo_espacio_id=${tipoEspacioId}`
      }

      const response = await ApiClient.get<CalendarioDisponibilidad>(url)
      return response
    },
    staleTime: 2 * 60 * 1000, // 2 minutos
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: !!fechaDesde && !!fechaHasta, // Solo ejecutar si hay fechas
  })
}

/**
 * Hook para obtener reservas por rango de fechas
 * Permite filtrar por estado y espacio
 *
 * @param fechaDesde - Fecha inicial (YYYY-MM-DD)
 * @param fechaHasta - Fecha final (YYYY-MM-DD)
 * @param estado - (Opcional) Filtrar por estado (confirmada, pendiente, etc.)
 * @param espacioId - (Opcional) Filtrar por espacio específico
 * @param tipoEspacioId - (Opcional) Filtrar por tipo de espacio
 */
export const useCalendarioReservas = (
  fechaDesde: string,
  fechaHasta: string,
  estado?: string,
  espacioId?: string,
  tipoEspacioId?: string
) => {
  return useQuery({
    queryKey: [
      CALENDARIO_QUERY_KEYS.RESERVAS,
      { fechaDesde, fechaHasta, estado, espacioId, tipoEspacioId },
    ],
    queryFn: async () => {
      let url = `/api/admin-protected/calendario/reservas?fecha_desde=${fechaDesde}&fecha_hasta=${fechaHasta}`

      if (estado) {
        url += `&estado=${estado}`
      }

      if (espacioId) {
        url += `&espacio_id=${espacioId}`
      }

      if (tipoEspacioId) {
        url += `&tipo_espacio_id=${tipoEspacioId}`
      }

      const response = await ApiClient.get<ReservaCalendario[]>(url)
      return response
    },
    staleTime: 2 * 60 * 1000, // 2 minutos
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: !!fechaDesde && !!fechaHasta, // Solo ejecutar si hay fechas
  })
}

/**
 * Hook para obtener resumen del calendario (tarjetas superiores)
 * Retorna: ocupación promedio, espacios ocupados/disponibles, total reservas del mes
 *
 * @param fechaDesde - Fecha inicial del mes (YYYY-MM-DD)
 * @param fechaHasta - Fecha final del mes (YYYY-MM-DD)
 * @param espacioId - (Opcional) Filtrar por espacio específico
 * @param tipoEspacioId - (Opcional) Filtrar por tipo de espacio
 */
export const useCalendarioResumen = (
  fechaDesde: string,
  fechaHasta: string,
  espacioId?: string,
  tipoEspacioId?: string
) => {
  return useQuery({
    queryKey: [
      CALENDARIO_QUERY_KEYS.RESUMEN,
      { fechaDesde, fechaHasta, espacioId, tipoEspacioId },
    ],
    queryFn: async () => {
      let url = `/api/admin-protected/calendario/resumen?fecha_desde=${fechaDesde}&fecha_hasta=${fechaHasta}`

      if (espacioId) {
        url += `&espacio_id=${espacioId}`
      }

      if (tipoEspacioId) {
        url += `&tipo_espacio_id=${tipoEspacioId}`
      }

      const response = await ApiClient.get<CalendarioResumen>(url)
      return response
    },
    staleTime: 5 * 60 * 1000, // 5 minutos
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: !!fechaDesde && !!fechaHasta,
  })
}

/**
 * Hook para obtener ocupación por día del mes
 * Retorna datos ligeros para las tarjetas del calendario
 *
 * @param fechaDesde - Fecha inicial del mes (YYYY-MM-DD)
 * @param fechaHasta - Fecha final del mes (YYYY-MM-DD)
 * @param espacioId - (Opcional) Filtrar por espacio específico
 * @param tipoEspacioId - (Opcional) Filtrar por tipo de espacio
 */
export const useCalendarioDiasOcupacion = (
  fechaDesde: string,
  fechaHasta: string,
  espacioId?: string,
  tipoEspacioId?: string
) => {
  return useQuery({
    queryKey: [
      CALENDARIO_QUERY_KEYS.DIAS_OCUPACION,
      { fechaDesde, fechaHasta, espacioId, tipoEspacioId },
    ],
    queryFn: async () => {
      let url = `/api/admin-protected/calendario/dias-ocupacion?fecha_desde=${fechaDesde}&fecha_hasta=${fechaHasta}`

      if (espacioId) {
        url += `&espacio_id=${espacioId}`
      }

      if (tipoEspacioId) {
        url += `&tipo_espacio_id=${tipoEspacioId}`
      }

      const response = await ApiClient.get<CalendarioDiasOcupacion>(url)
      return response
    },
    staleTime: 5 * 60 * 1000, // 5 minutos
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: !!fechaDesde && !!fechaHasta,
  })
}

// =============================================================================
// UTILIDADES
// =============================================================================

/**
 * Calcula el número de noches entre dos fechas
 */
export const calcularNoches = (fechaEntrada: string, fechaSalida: string): number => {
  const entrada = parseLocalDateString(fechaEntrada)
  const salida = parseLocalDateString(fechaSalida)
  const diffTime = Math.abs(salida.getTime() - entrada.getTime())
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
}

/**
 * Convierte una fecha YYYY-MM-DD a formato más legible
 */
export const formatearFecha = (fecha: string, locale: string = 'es-CL'): string => {
  return parseLocalDateString(fecha).toLocaleDateString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

/**
 * Obtiene color de estado para mostrar en el calendario
 */
export const getColorEstado = (estado: string): string => {
  switch (estado) {
    case 'confirmada':
      return '#22c55e' // green
    case 'en_proceso':
      return '#3b82f6' // blue
    case 'pendiente':
      return '#eab308' // yellow
    case 'cancelada':
      return '#ef4444' // red
    case 'completada':
      return '#a855f7' // purple
    default:
      return '#6b7280' // gray
  }
}

/**
 * Obtiene color de estado de pago para mostrar en el calendario
 */
export const getColorEstadoPago = (estado: string): string => {
  switch (estado) {
    case 'pagado':
      return '#22c55e' // green
    case 'pendiente':
      return '#eab308' // yellow
    case 'reembolsado':
      return '#f97316' // orange
    default:
      return '#6b7280' // gray
  }
}

/**
 * Verifica si una fecha está dentro de un rango
 */
export const estaEnRango = (fecha: string, inicio: string, fin: string): boolean => {
  const fechaObj = parseLocalDateString(fecha)
  const inicioObj = parseLocalDateString(inicio)
  const finObj = parseLocalDateString(fin)

  return fechaObj >= inicioObj && fechaObj <= finObj
}

/**
 * Obtiene las fechas que se solapan entre dos rangos
 */
export const getSolapamiento = (
  inicio1: string,
  fin1: string,
  inicio2: string,
  fin2: string
): { solapamiendo: boolean; diasSolapados: number } => {
  const start1 = parseLocalDateString(inicio1).getTime()
  const end1 = parseLocalDateString(fin1).getTime()
  const start2 = parseLocalDateString(inicio2).getTime()
  const end2 = parseLocalDateString(fin2).getTime()

  // Si no se solapan
  if (end1 < start2 || end2 < start1) {
    return { solapamiendo: false, diasSolapados: 0 }
  }

  // Calcular días de solapamiento
  const overlap_start = Math.max(start1, start2)
  const overlap_end = Math.min(end1, end2)
  const diasSolapados = Math.ceil((overlap_end - overlap_start) / (1000 * 60 * 60 * 24))

  return { solapamiendo: true, diasSolapados: Math.max(1, diasSolapados) }
}
